import { AxisBottom } from '@visx/axis'
import { ParentSize } from '@visx/responsive'
import { scaleTime } from '@visx/scale'
import { timeDay, timeHour, timeMonth, timeYear } from 'd3'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useIsMobile } from '@/hooks/use-mobile'
import { fittingTicks, spacedTicks, tickLabelShift } from './axis-ticks'
import { CARD_WIDTH, layoutEvents, leaderCardY } from './layout-events'
import { TimelineClusterBadge } from './TimelineClusterBadge'
import {
  type TimelineCardEvent,
  TimelineEventCard,
  type TimelineHoverPayload,
} from './TimelineEventCard'
import { useTimelineTouch } from './useTimelineTouch'
import type { Viewport } from './useTimelineViewport'

interface TimelineCanvasProps {
  events: readonly TimelineCardEvent[]
  viewport: Viewport
  selectedEdgeId: string | null
  onSelectEdge: (edgeId: string, anchor: HTMLElement) => void
  onSelectNode: (nodeId: string, anchor: HTMLElement) => void
  onZoomAtCursor: (cursorFraction: number, factor: number) => void
  onPanBy: (dxPx: number, widthPx: number) => void
  onFitAll: () => void
  onHoverEnter?: (payload: TimelineHoverPayload) => void
  onHoverLeave?: () => void
  tickDensity?: 'auto' | 'year' | 'month' | 'day' | 'hour'
}

const MONTH_ABBR = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
]
const HR = 3_600_000
const DAY = 24 * HR

/** Axis tick formatter — always carries the year. */
function tickLabel(d: Date, durationMs: number): string {
  const month = MONTH_ABBR[d.getMonth()]
  const day = String(d.getDate()).padStart(2, '0')
  const year = d.getFullYear()
  const hh = String(d.getHours()).padStart(2, '0')
  const mm = String(d.getMinutes()).padStart(2, '0')
  if (durationMs > 5 * 365 * DAY) return String(year)
  if (durationMs > 120 * DAY) return `${month} ${year}`
  if (durationMs > 2 * DAY) return `${month} ${day}, ${year}`
  if (durationMs > HR) return `${month} ${day} ${hh}:${mm}`
  return `${hh}:${mm}:${String(d.getSeconds()).padStart(2, '0')}`
}

export function TimelineCanvas(props: TimelineCanvasProps) {
  return (
    <ParentSize>
      {({ width, height }) =>
        width > 0 && height > 0 ? (
          <TimelineCanvasInner {...props} width={width} height={height} />
        ) : null
      }
    </ParentSize>
  )
}

interface InnerProps extends TimelineCanvasProps {
  width: number
  height: number
}

function TimelineCanvasInner({
  events,
  viewport,
  selectedEdgeId,
  onSelectEdge,
  onSelectNode,
  onZoomAtCursor,
  onPanBy,
  onFitAll,
  onHoverEnter,
  onHoverLeave,
  width,
  height,
  tickDensity = 'auto',
}: InnerProps) {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const isPhone = useIsMobile()

  const xScale = useMemo(
    () =>
      scaleTime({
        domain: [new Date(viewport.start), new Date(viewport.end)],
        range: [0, width],
      }),
    [viewport.start, viewport.end, width]
  )

  const axisY = Math.floor(height / 2)

  const layout = useMemo(
    () => layoutEvents(events, ts => xScale(new Date(ts)) as number),
    [events, xScale]
  )

  // Fast lookup for cluster popovers: when a cluster contains members
  // {a,b,c}, the popover renders the projected card events. Building this
  // map once per ``events`` change keeps the lookup O(1) inside the loop.
  const eventById = useMemo(() => {
    const m = new Map<string, TimelineCardEvent>()
    for (const ev of events) m.set(ev.id, ev)
    return m
  }, [events])

  // At most one cluster is expanded at a time. The user's spec: clusters
  // collapse automatically when the axis is moved. The simplest correct
  // implementation is to close any open cluster whenever the viewport
  // window (start/end) changes — covers pan, zoom, fit-all, and the
  // time-range scrubber from a single dependency.
  const [openClusterId, setOpenClusterId] = useState<string | null>(null)
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional
  // — we close on viewport.start/end changes only, not on setOpenClusterId
  // identity changes (state setters are stable).
  useEffect(() => {
    setOpenClusterId(null)
  }, [viewport.start, viewport.end])
  // On a phone a selection opens the sidebar over the whole canvas, and an
  // open cluster list would stay on top of it — so picking a member closes
  // the list there. On wider screens it stays open for browsing.
  const selectClusterMember = useCallback(
    (edgeId: string, anchor: HTMLElement) => {
      if (isPhone) setOpenClusterId(null)
      onSelectEdge(edgeId, anchor)
    },
    [isPhone, onSelectEdge]
  )

  const durationMs = viewport.end - viewport.start
  const numTicks = Math.max(5, Math.min(12, Math.floor(width / 140)))

  // When tick density is forced, supply explicit Date[] ticks from the
  // matching d3-time interval, choosing a step that keeps tick count near
  // `numTicks` so labels don't overlap.
  const densityTicks = useMemo<Date[] | undefined>(() => {
    if (tickDensity === 'auto') return undefined
    const interval =
      tickDensity === 'year'
        ? timeYear
        : tickDensity === 'month'
          ? timeMonth
          : tickDensity === 'day'
            ? timeDay
            : timeHour
    const start = new Date(viewport.start)
    const end = new Date(viewport.end)
    const count = interval.count(start, end)
    if (count <= 0) return [interval.floor(start)]
    const step = Math.max(1, Math.ceil(count / numTicks))
    const every = interval.every(step)
    return every ? every.range(interval.floor(start), end) : interval.range(start, end)
  }, [tickDensity, viewport.start, viewport.end, numTicks])

  // A phone axis cannot hold the 5-10 labels d3 hands back whatever the
  // width (72 px labels ~50 px apart smear into each other), so it draws
  // the smallest calendar step that fits. Desktop keeps d3's own ticks.
  const tickAxis = useMemo(
    () => ({
      x: (tick: Date) => xScale(tick) as number,
      width,
      label: (tick: Date) => tickLabel(tick, durationMs),
    }),
    [xScale, width, durationMs]
  )
  const tickValues = useMemo<Date[] | undefined>(() => {
    if (!isPhone) return densityTicks
    if (densityTicks) return spacedTicks(densityTicks, tickAxis)
    return fittingTicks(viewport.start, viewport.end, tickAxis)
  }, [isPhone, densityTicks, viewport.start, viewport.end, tickAxis])

  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    function onWheel(e: WheelEvent) {
      e.preventDefault()
      const rect = el.getBoundingClientRect()
      const cursorFrac = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width))
      const factor = e.deltaY < 0 ? 0.8 : 1.25
      onZoomAtCursor(cursorFrac, factor)
    }
    el.addEventListener('wheel', onWheel, { passive: false })
    return () => el.removeEventListener('wheel', onWheel)
  }, [onZoomAtCursor])

  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    let dragging = false
    let lastX = 0
    function onDown(e: MouseEvent) {
      if ((e.target as HTMLElement).closest('[data-event-card]')) return
      dragging = true
      lastX = e.clientX
      document.body.style.cursor = 'grabbing'
    }
    function onMove(e: MouseEvent) {
      if (!dragging) return
      const dx = e.clientX - lastX
      lastX = e.clientX
      onPanBy(dx, el.getBoundingClientRect().width)
    }
    function onUp() {
      if (!dragging) return
      dragging = false
      document.body.style.cursor = ''
    }
    el.addEventListener('mousedown', onDown)
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
    return () => {
      el.removeEventListener('mousedown', onDown)
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onUp)
    }
  }, [onPanBy])

  // Touch screens: one-finger pan, two-finger pinch (no mouse or wheel there).
  useTimelineTouch(containerRef, { onPanBy, onZoomAtCursor })

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === '0' && !e.ctrlKey && !e.metaKey && !e.altKey) {
        const target = e.target as HTMLElement | null
        if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) return
        onFitAll()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onFitAll])

  const onDoubleClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if ((e.target as HTMLElement).closest('[data-event-card]')) return
      const rect = containerRef.current?.getBoundingClientRect()
      if (!rect) return
      const cursorFrac = (e.clientX - rect.left) / rect.width
      onZoomAtCursor(cursorFrac, 0.5)
    },
    [onZoomAtCursor]
  )

  return (
    <div
      ref={containerRef}
      className="relative h-full w-full cursor-grab touch-none overflow-hidden select-none bg-background"
      onDoubleClick={onDoubleClick}
    >
      <svg className="pointer-events-none absolute inset-0" width={width} height={height}>
        <line x1={0} x2={width} y1={axisY} y2={axisY} className="stroke-border" strokeWidth={1} />

        <AxisBottom
          top={axisY}
          scale={xScale}
          stroke="currentColor"
          tickStroke="currentColor"
          strokeWidth={1}
          tickLength={4}
          numTicks={numTicks}
          tickValues={tickValues}
          tickFormat={d => tickLabel(d as Date, durationMs)}
          tickLabelProps={tick => ({
            className: 'fill-muted-foreground font-mono tabular-nums',
            fontSize: 10,
            textAnchor: 'middle',
            // Phone: a label next to an edge is nudged inward, not cut off.
            dx: isPhone ? tickLabelShift(tick as Date, tickAxis) : undefined,
            dy: '0.9em',
          })}
          axisLineClassName="text-border"
          tickClassName="text-border"
        />

        <g className="text-muted-foreground">
          {Array.from(layout.values()).map(l => {
            if (l.x < -CARD_WIDTH || l.x > width + CARD_WIDTH) return null
            const cardY = leaderCardY(axisY, l)
            const y1 = l.lane === 'above' ? cardY : axisY
            const y2 = l.lane === 'above' ? axisY : cardY

            // Cluster: dot on axis + leader line up to the badge. No
            // interval bar — clusters only form from EXACT-same-timestamp
            // groups, which by definition collapse to one instant.
            if (l.kind === 'cluster') {
              return (
                <g key={l.id} className="text-primary/70">
                  <line x1={l.x} x2={l.x} y1={y1} y2={y2} stroke="currentColor" strokeWidth={1} />
                  <circle cx={l.x} cy={axisY} r={4} fill="currentColor" />
                </g>
              )
            }

            const ev = eventById.get(l.id)
            if (!ev) return null
            const isSel = selectedEdgeId === ev.id

            // Interval events draw a horizontal bar from start to end on the
            // axis row. The card sits at the start and is connected by the
            // usual leader line; the bar visually conveys duration.
            if (ev.isInterval && ev.endTimestamp) {
              const xEnd = xScale(new Date(ev.endTimestamp)) as number
              const xLeft = Math.min(l.x, xEnd)
              const xRight = Math.max(l.x, xEnd)
              const barY = axisY
              return (
                <g key={ev.id} className={isSel ? 'text-primary' : 'text-muted-foreground/60'}>
                  <line
                    x1={xLeft}
                    x2={xRight}
                    y1={barY}
                    y2={barY}
                    stroke="currentColor"
                    strokeWidth={isSel ? 4 : 3}
                    strokeLinecap="round"
                    opacity={0.85}
                  />
                  <line
                    x1={l.x}
                    x2={l.x}
                    y1={y1}
                    y2={y2}
                    stroke="currentColor"
                    strokeWidth={isSel ? 1.5 : 1}
                  />
                  <circle cx={l.x} cy={axisY} r={isSel ? 4 : 3} fill="currentColor" />
                  <circle cx={xEnd} cy={axisY} r={isSel ? 4 : 3} fill="currentColor" />
                </g>
              )
            }

            return (
              <g key={ev.id} className={isSel ? 'text-primary' : 'text-muted-foreground/60'}>
                <line
                  x1={l.x}
                  x2={l.x}
                  y1={y1}
                  y2={y2}
                  stroke="currentColor"
                  strokeWidth={isSel ? 1.5 : 1}
                />
                <circle cx={l.x} cy={axisY} r={isSel ? 4 : 3} fill="currentColor" />
              </g>
            )
          })}
        </g>
      </svg>

      <div className="pointer-events-none absolute inset-0">
        {Array.from(layout.values()).map(l => {
          if (l.x < -CARD_WIDTH || l.x > width + CARD_WIDTH) return null

          if (l.kind === 'cluster') {
            const members = l.memberIds
              .map(id => eventById.get(id))
              .filter((m): m is TimelineCardEvent => m !== undefined)
            return (
              <div key={l.id} className="pointer-events-auto">
                <TimelineClusterBadge
                  layout={l}
                  axisY={axisY}
                  members={members}
                  isOpen={openClusterId === l.id}
                  onOpenChange={open => setOpenClusterId(open ? l.id : null)}
                  onSelectMember={selectClusterMember}
                />
              </div>
            )
          }

          const ev = eventById.get(l.id)
          if (!ev) return null
          return (
            <div key={ev.id} className="pointer-events-auto">
              <TimelineEventCard
                event={ev}
                layout={l}
                axisY={axisY}
                isSelected={selectedEdgeId === ev.id}
                onSelectEdge={onSelectEdge}
                onSelectNode={onSelectNode}
                onHoverEnter={onHoverEnter}
                onHoverLeave={onHoverLeave}
              />
            </div>
          )
        })}
      </div>
    </div>
  )
}

export type { TimelineCardEvent, TimelineHoverPayload, Viewport }
